import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { spacing } from '@rbilabs/universal-components/themes';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { isEmpty } from 'lodash-es';
import { useIntl } from 'react-intl';
import { FlatList, StyleSheet } from 'react-native';

import {
  CenteredRowContainer,
  centeredRowContainerStyles,
} from 'components/layout/centered-row-container';
import ConfettiFriesAnimation from 'components/lottie-animations/confetti-fries';
import { useConfettiFriesAnimation } from 'components/lottie-animations/confetti-fries/use-confetti-fries-animation';
import { usePullToReload } from 'components/pull-to-reload';
import { toaster } from 'components/toaster/toaster';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { useCart } from 'hooks/use-cart';
import { useFocusEffect } from 'navigation/hooks/use-focus-effect';
import { CustomEventNames, logLoyaltyEvent } from 'state/amplitude';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { LoyaltyOfferWithInlineAlertMessage } from 'state/loyalty/hooks/types';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { pushUnavailableOffersToBottom } from 'state/loyalty/hooks/utils/helpers';
import {
  pushWalmartOffersToTop,
  pushWalmartWeekOffersToTop,
} from 'state/loyalty/hooks/utils/walmart';
import {
  ICartEntryType,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import { IEngineOffer } from 'state/loyalty/offers/types';
import { LoyaltyOffer, LoyaltyOfferGroup } from 'state/loyalty/types';
import { isDiscountLoyaltyOffer } from 'state/loyalty/utils';
import { WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';
import { stringToBool } from 'utils/string-To-Bool';

import OffersEmptyScreen from './empty-screen/empty-screen';
import EndOfOffers from './end-of-offers/loyalty-end-of-offers';
import { LoyaltyListItem } from './loyalty-list-item';
import { LoyaltyOffersSkeletonLoader } from './loyalty-offers-skeleton-loader';
import { LoyaltyPromoCode } from './loyalty-promo-code';
import { OffersInCart } from './offers-in-cart/offers-in-cart';
import { useGetCmsAndLoyaltyOffers } from './use-get-cms-and-loyalty-offers';
import { useGetIncentive } from './use-get-incentive';
import useLoyaltyOffersFlow from './use-loyalty-offers-flow';
import { useReduceOffers } from './use-offers-reducer';
import { mergeOffersAndOfferGroups } from './utils';

export const LoyaltyOffersBase = () => {
  const { formatMessage } = useIntl();
  const { loyaltyStandardOffersEnabled } = useLoyaltyContext();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { loyaltyUser, refetch: refetchLoyaltyUser } = useLoyaltyUserState({
    refreshOnMountOrFocus: true,
  });

  const shouldRefetchOffers = useAppSelector(selectors.loyalty.selectShouldRefetchOffers);
  const dispatch = useAppDispatch();
  const {
    inRestaurantRedemptionCart,
    removeInRestaurantRedemptionEntry,
  } = useInRestaurantRedemptionContext();

  const enableLoyaltyPromoCodes = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_PROMO_CODES);
  const { removeFromCart } = useCart();

  const router = useRouter();
  const { offerEngineId: loyaltyEngineId } = useLoyaltyOffersFlow();
  const skipAnimation = useRef(true);

  const { triggerAnimation } = useConfettiFriesAnimation();
  const queryParams = useLocalSearchParams<{ success?: string }>();
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(
    !!stringToBool(queryParams.success)
  );

  useFocusEffect(
    React.useCallback(() => {
      emitEvent(EventName.SCREEN_RENDER, {
        screenId: 'loyaltyOffers',
      });
    }, [])
  );

  const {
    cmsOffers,
    cmsOfferGroups,
    personalizedOffers,
    baseOffers,
    offersLoading,
    refreshOffers,
    refreshOffersLoading,
  } = useGetCmsAndLoyaltyOffers();

  const { offers, offersInCart, offerGroups, reductionLoading, reprocessOffers } = useReduceOffers({
    cmsOffers,
    cmsOfferGroups,
    personalizedOffers,
    baseOffers,
    offersLoading,
  });

  const { selectedIncentive } = useGetIncentive({ loyaltyEngineId });

  const offersAreLoading = offersLoading || reductionLoading || refreshOffersLoading;

  // TODO: extract this method to a reusable place
  const removeLoyaltyOffer = useCallback(
    (offerToRemove: LoyaltyOffer) => {
      const appliedOfferToRemove = appliedOffers.find(
        offer => offer.id === offerToRemove.loyaltyEngineId
      );

      if (appliedOfferToRemove?.cartId) {
        // Remove applied offer...
        dispatch(actions.loyalty.removeAppliedOffer(appliedOfferToRemove));
        // Remove it from the cart...
        removeFromCart({ cartId: appliedOfferToRemove.cartId });
        return true;
      }

      return false;
    },
    [appliedOffers, dispatch, removeFromCart]
  );

  useEffect(() => {
    // If walmart+ user was linked successfully and not partner data we do need to refetch user data.
    if (!!queryParams.success && !!loyaltyUser?.partnerUserData) {
      refetchLoyaltyUser();
    }
  }, [loyaltyUser?.partnerUserData, queryParams.success, refetchLoyaltyUser]);

  const handleRemove = useCallback(
    (offerToRemove: LoyaltyOffer) => {
      const redemptionOfferToRemove = inRestaurantRedemptionCart.find(
        cartEntry =>
          cartEntry.type === ICartEntryType.OFFER &&
          cartEntry.details.offer._id === offerToRemove._id
      );

      let showNotification = false;

      if (redemptionOfferToRemove) {
        removeInRestaurantRedemptionEntry(redemptionOfferToRemove);
        showNotification = true;
      } else if (isDiscountLoyaltyOffer(offerToRemove)) {
        dispatch(actions.loyalty.setSelectedOffer(null));
        dispatch(actions.loyalty.resetAppliedDiscountOffers());
        reprocessOffers();
        showNotification = true;
      } else {
        reprocessOffers();
        showNotification = removeLoyaltyOffer(offerToRemove);
      }

      if (showNotification) {
        toaster.positive(formatMessage({ id: 'offerAppliedSuccess' }));
      }
    },
    [
      inRestaurantRedemptionCart,
      removeInRestaurantRedemptionEntry,
      dispatch,
      removeLoyaltyOffer,
      formatMessage,
      reprocessOffers,
    ]
  );

  // Refetch offers if shouldRefetchOffers is true or an offer is added to cart
  // also responsible for initial loading of offers
  useEffect(() => {
    if (shouldRefetchOffers) {
      refreshOffers();
    }
  }, [refreshOffers, shouldRefetchOffers]);

  // Refetch offers if an offer is added to cart
  useEffect(() => {
    if (offersInCart?.length && !offers.length) {
      dispatch(actions.loyalty.setShouldRefetchOffers(true));
    }
  }, [offersInCart?.length, offers.length, dispatch]);

  const handleOfferPress = useCallback(
    (incentive: LoyaltyOfferWithInlineAlertMessage) => {
      if (incentive.inlineAlertMessage) {
        return;
      }
      const incentiveId = incentive._id;
      const loyaltyEngineId = incentive.loyaltyEngineId;
      if (incentiveId !== selectedIncentive?._id) {
        logLoyaltyEvent({
          name: CustomEventNames.OFFER_SELECTED,
          incentives: [incentive],
          attributes: {
            serviceMode: incentive.offerServiceMode,
          },
        });
        router.navigate(`${routes.loyaltyOfferList}/${loyaltyEngineId}`);
      }
    },
    [router, selectedIncentive]
  );

  const handleGroupPress = useCallback(
    (group: LoyaltyOfferGroup) => {
      router.navigate(`${routes.loyaltyOfferGroup}/${group.groupId}`);
    },
    [router]
  );

  // Log event when offers are displayed
  useEffect(() => {
    if (offers.length > 0 && !offersAreLoading) {
      logLoyaltyEvent(
        {
          name: CustomEventNames.OFFERS_DISPLAYED,
          incentives: offers,
          attributes: {
            offerCount: offers.length,
          },
        },
        { skipLoggingToBraze: true }
      );
    }
  }, [offers, offersAreLoading]);

  useRumPageView(
    `loyalty-offers${selectedIncentive ? '-detail' : '-list'}`,
    `Loyalty Offers${selectedIncentive ? ' Detail' : ' List'}`
  );

  useEffect(() => {
    if (
      loyaltyEngineId &&
      !selectedIncentive &&
      loyaltyStandardOffersEnabled &&
      !offersLoading &&
      !isEmpty(offers)
    ) {
      toaster.negative(
        formatMessage(
          { id: 'incentiveFeedbackInvalidStore' },
          { incentiveType: formatMessage({ id: 'offer' }) }
        )
      );
    }
  }, [
    formatMessage,
    loyaltyEngineId,
    loyaltyStandardOffersEnabled,
    router,
    offers,
    offersLoading,
    selectedIncentive,
  ]);

  const renderItem = useCallback(
    ({ item }: { item: LoyaltyOffer | LoyaltyOfferGroup }) => {
      return (
        <LoyaltyListItem
          item={item}
          handleOfferPress={handleOfferPress}
          handleGroupPress={handleGroupPress}
          skipAnimation={skipAnimation.current}
        />
      );
    },
    [handleOfferPress, handleGroupPress]
  );

  const ListHeaderComponent = useCallback(
    () => (
      <>
        {
          /* If it's a widget, don't allow to pull on reload */
          offersInCart?.length ? (
            <OffersInCart
              offers={offersInCart}
              handlePress={handleOfferPress}
              handleRemove={handleRemove}
            />
          ) : null
        }
      </>
    ),
    [handleOfferPress, handleRemove, offersInCart]
  );

  const sortedOffers = useMemo(() => {
    if (!offers) {
      return null;
    }
    const mergedOffers = mergeOffersAndOfferGroups(offers, offerGroups);
    mergedOffers.sort(pushWalmartOffersToTop);
    mergedOffers.sort(pushWalmartWeekOffersToTop);
    mergedOffers.sort(pushUnavailableOffersToBottom);
    return mergedOffers;
  }, [offers, offerGroups]);

  const { refreshControl, isRefreshing } = usePullToReload({
    onRefresh: refreshOffers,
  });

  const showLoader = !isRefreshing && (offersAreLoading || offers.length === 0);

  // Trigger confetti fries animation and W+ toast message based on `success` query parameter
  useEffect(() => {
    if (showSuccessAnimation && loyaltyStandardOffersEnabled && !showLoader) {
      triggerAnimation();
      toaster.positive(formatMessage({ id: 'walmartEnjoyYourOffers' }));
      setShowSuccessAnimation(false);
      router.setParams({ success: undefined });
    }
  }, [
    formatMessage,
    loyaltyStandardOffersEnabled,
    router,
    showLoader,
    showSuccessAnimation,
    triggerAnimation,
  ]);

  if (!loyaltyStandardOffersEnabled) {
    // TODO: show empty state if Loyalty is not enabled
    return null;
  }

  if (showLoader) {
    return <LoyaltyOffersSkeletonLoader />;
  }

  const onPromoCodeRedeemSuccess = ({
    configOffer,
    engineOffer,
  }: {
    configOffer: LoyaltyOffer;
    engineOffer: IEngineOffer;
  }) => {
    skipAnimation.current = false;
    toaster.positive(formatMessage({ id: 'promoCodeAddedToOffers' }));
    dispatch(actions.loyalty.setCmsOffers([configOffer]));
    dispatch(
      actions.loyalty.unshiftPersonalizedOffer({
        ...configOffer,
        metadata: engineOffer.metadata,
        loyaltyEngineId: engineOffer.id,
        serviceModes: engineOffer.serviceModes,
        errors: engineOffer.errors,
      })
    );
  };

  const canShowPromoCode = enableLoyaltyPromoCodes && !selectedIncentive;

  return (
    <>
      {canShowPromoCode && (
        <CenteredRowContainer standardPadding style={styles.header}>
          <LoyaltyPromoCode onPromoCodeRedeemSuccess={onPromoCodeRedeemSuccess} />
        </CenteredRowContainer>
      )}
      {sortedOffers ? (
        <FlatList
          ListHeaderComponent={ListHeaderComponent}
          ListFooterComponent={EndOfOffers}
          contentContainerStyle={styles.flatList}
          refreshControl={refreshControl}
          data={sortedOffers}
          initialNumToRender={5}
          keyExtractor={(item, index) => item._id || `${index}`}
          renderItem={renderItem}
        />
      ) : (
        <OffersEmptyScreen />
      )}
      <ConfettiFriesAnimation />
    </>
  );
};

const styles = StyleSheet.create({
  header: { marginTop: spacing.$2, maxWidth: WEB_SMALL_MAX_WIDTH },
  flatList: {
    ...centeredRowContainerStyles.content,
    maxWidth: WEB_SMALL_MAX_WIDTH,
    paddingHorizontal: spacing.$4,
  },
});
